import React, { useState, useEffect } from 'react';
import {
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  Chip,
  IconButton,
  Avatar,
  Tooltip,
  Divider,
  TableSortLabel,
} from '@mui/material';
import {
  DragIndicator as DragIndicatorIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Tune as TuneIcon,
  Article as ArticleIcon,
  DeleteOutline as DeleteIcon,
} from '@mui/icons-material';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { useTheme } from '../../../../design-system/providers/ThemeProvider';
import PlatformDialog from '../../../molecules/PlatformDialog/PlatformDialog';
import PlatformDialogTitle from '../../../molecules/PlatformDialog/PlatformDialogTitle';
import { getEffectiveTier } from '../../../../utils/effectiveTier';

const normalizeTag = (tag) => String(tag || '').trim().toLocaleLowerCase('pt-BR');

const getArticleTags = (article) => (article.tags || [])
  .map(tag => typeof tag === 'object' ? (tag.name || tag._id) : tag)
  .filter(Boolean);

const getMainTag = (article, tagOrder = []) => {
  const articleTags = getArticleTags(article);
  if (articleTags.length === 0) return null;

  const tagsByKey = new Map(articleTags.map(tag => [normalizeTag(tag), tag]));
  for (const orderedTag of tagOrder) {
    const match = tagsByKey.get(normalizeTag(orderedTag));
    if (match) return match;
  }

  return articleTags[0];
};

const getTagGroupKey = (article, tagOrder = []) => {
  const tag = getMainTag(article, tagOrder);
  return tag ? normalizeTag(tag) : '__no_tag__';
};

const groupArticlesByTag = (articles, tagOrder = []) => {
  const groups = new Map();
  articles.forEach(article => {
    const key = getTagGroupKey(article, tagOrder);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(article);
  });

  const orderedKeys = tagOrder.map(normalizeTag).filter(key => groups.has(key));
  const remainingKeys = Array.from(groups.keys()).filter(
    key => key !== '__no_tag__' && !orderedKeys.includes(key)
  );
  const finalKeys = [...orderedKeys, ...remainingKeys];
  if (groups.has('__no_tag__')) finalKeys.push('__no_tag__');

  return finalKeys.flatMap(key => groups.get(key));
};

/**
 * NewsletterFinalOrderDialog
 *
 * Modal de ajuste fino da ordem dos artigos.
 * Mostra a prévia da ordem atual (pós-gabarito) com drag & drop
 * para ajustes manuais antes do envio.
 */
const NewsletterFinalOrderDialog = ({ open, onClose, articles = [], tagOrder = [], onConfirm }) => {
  const { 
    currentTheme, 
    getPrimaryBackground, 
    isDarkMode, 
    gradients,
    isPrimaryGradient 
  } = useTheme();
  const [orderedArticles, setOrderedArticles] = useState([]);
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');

  // Sincronizar com os artigos recebidos ao abrir
  useEffect(() => {
    if (open) {
      setOrderedArticles(groupArticlesByTag(articles, tagOrder));
      setSortField(null);
      setSortDirection('asc');
    }
  }, [open, articles, tagOrder]);

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const sourceArticle = orderedArticles[result.source.index];
    const destinationArticle = orderedArticles[result.destination.index];
    if (getTagGroupKey(sourceArticle, tagOrder) !== getTagGroupKey(destinationArticle, tagOrder)) return;

    const items = Array.from(orderedArticles);
    const [moved] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, moved);
    setOrderedArticles(items);
    setSortField(null);
  };

  const handleRemove = (id) => {
    setOrderedArticles(prev => prev.filter(a => a._id !== id));
  };

  const handleConfirm = () => {
    onConfirm(orderedArticles);
  };


  const handleSort = (field) => {
    const nextDirection = field === 'tag'
      ? 'asc'
      : (sortField === field && sortDirection === 'asc' ? 'desc' : 'asc');
    const directionFactor = nextDirection === 'asc' ? 1 : -1;

    setOrderedArticles(prev => {
      const groups = new Map();
      prev.forEach(article => {
        const key = getTagGroupKey(article, tagOrder);
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(article);
      });

      if (field === 'tag') {
        return groupArticlesByTag(prev, tagOrder);
      }

      const getValue = (item) => field === 'mediaType'
        ? (item.mediaType || '')
        : (new Date(item.date || item.createdAt || 0).getTime() || 0);

      return Array.from(groups.values()).flatMap(group => group
        .map((article, index) => ({ article, index }))
        .sort((a, b) => {
          const aValue = getValue(a.article);
          const bValue = getValue(b.article);
          const comparison = field === 'date'
            ? aValue - bValue
            : aValue.localeCompare(bValue, 'pt-BR', { sensitivity: 'base', numeric: true });
          return comparison === 0 ? a.index - b.index : comparison * directionFactor;
        })
        .map(({ article }) => article));
    });

    setSortField(field);
    setSortDirection(nextDirection);
  };
  // Cor de chip de tier alinhada ao design system
  const tierColor = (tier) => {
    const tierStr = String(tier || '').toLowerCase();
    if (tierStr.includes('1')) return 'success';
    if (tierStr.includes('2')) return 'warning';
    if (tierStr.includes('3')) return 'info';
    return 'default';
  };

  return (
    <PlatformDialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { maxHeight: '90vh' }
      }}
    >
      <PlatformDialogTitle
        title="Ajuste Fino da Ordem"
        subtitle={`${orderedArticles.length} artigo(s) selecionado(s) • Arraste para reordenar`}
        icon={<TuneIcon />}
        onClose={onClose}
      />

      {/* Instrução sutil */}
      <Box sx={{ px: 3, py: 1.5, bgcolor: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.01)', borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="body2" color="text.secondary" fontWeight={500}>
          Os blocos seguem a sequência de Tags definida no Gabarito. As outras ordenações são aplicadas dentro de cada bloco.
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, mt: 1.25, flexWrap: 'wrap' }}>
          <Typography variant="caption" color="text.secondary" fontWeight={700}>
            Ordenar por:
          </Typography>
          <TableSortLabel active={sortField === 'tag'} direction={sortField === 'tag' ? sortDirection : 'asc'} onClick={() => handleSort('tag')}>
            <Typography variant="caption" fontWeight={700}>Tag</Typography>
          </TableSortLabel>
          <TableSortLabel active={sortField === 'mediaType'} direction={sortField === 'mediaType' ? sortDirection : 'asc'} onClick={() => handleSort('mediaType')}>
            <Typography variant="caption" fontWeight={700}>Tipo de mídia</Typography>
          </TableSortLabel>
          <TableSortLabel active={sortField === 'date'} direction={sortField === 'date' ? sortDirection : 'asc'} onClick={() => handleSort('date')}>
            <Typography variant="caption" fontWeight={700}>Data</Typography>
          </TableSortLabel>
          {sortField && (
            <Typography variant="caption" color="primary.main" fontWeight={600}>
              {sortDirection === 'asc' ? 'Crescente' : 'Decrescente'}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Lista de artigos com drag & drop */}
      <DialogContent sx={{ p: 2, overflowY: 'auto', flex: 1, bgcolor: 'background.default' }}>
        {orderedArticles.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8, opacity: 0.5 }}>
            <ArticleIcon sx={{ fontSize: 64, mb: 2 }} />
            <Typography variant="h6">Nenhum artigo na lista</Typography>
          </Box>
        ) : (
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="final-order-articles">
              {(provided, snapshot) => (
                <Box
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  sx={{
                    minHeight: 100,
                    p: 0.5,
                  }}
                >
                  {orderedArticles.map((article, index) => {
                    const tag = getMainTag(article, tagOrder);
                    return (
                      <Draggable key={article._id} draggableId={article._id} index={index}>
                        {(provided, snapshot) => (
                          <Paper
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            elevation={snapshot.isDragging ? 12 : 0}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 2,
                              p: 1.5,
                              mb: 1.5,
                              borderRadius: 2,
                              bgcolor: snapshot.isDragging 
                                ? (isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.02)')
                                : 'background.paper',
                              border: '1px solid',
                              borderColor: snapshot.isDragging ? 'primary.main' : 'divider',
                              transition: 'all 0.2s ease',
                              position: 'relative',
                              overflow: 'hidden',
                              '&:hover': {
                                borderColor: 'primary.light',
                                bgcolor: isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.01)',
                              },
                              '&:last-child': { mb: 0 },
                            }}
                          >
                            {/* Drag handle e Número */}
                            <Box
                              {...provided.dragHandleProps}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                color: 'text.disabled',
                                flexShrink: 0,
                                cursor: 'grab',
                                '&:active': { cursor: 'grabbing' }
                              }}
                            >
                              <DragIndicatorIcon fontSize="small" />
                              <Avatar
                                sx={{
                                  width: 24,
                                  height: 24,
                                  fontSize: '0.7rem',
                                  fontWeight: 800,
                                  bgcolor: snapshot.isDragging ? 'primary.main' : 'action.selected',
                                  color: snapshot.isDragging ? 'primary.contrastText' : 'text.primary',
                                  transition: 'all 0.2s ease',
                                }}
                              >
                                {index + 1}
                              </Avatar>
                            </Box>

                            {/* Thumbnail */}
                            <Avatar
                              src={article.image || article.thumbnail}
                              variant="rounded"
                              sx={{ 
                                width: 50, 
                                height: 50, 
                                flexShrink: 0,
                                borderRadius: 1.5,
                                border: '1px solid',
                                borderColor: 'divider'
                              }}
                            >
                              <ArticleIcon />
                            </Avatar>

                            {/* Conteúdo */}
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography
                                variant="body2"
                                fontWeight={600}
                                noWrap
                                sx={{ mb: 0.5 }}
                              >
                                {article.title}
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                <Typography
                                  variant="caption"
                                  sx={{ color: 'text.secondary', fontWeight: 500 }}
                                >
                                  {article.siteName || '-'}
                                </Typography>
                                
                                {getEffectiveTier(article) && (
                                  <Chip
                                    label={`Tier ${getEffectiveTier(article)}`}
                                    size="small"
                                    color={tierColor(getEffectiveTier(article))}
                                    variant="outlined"
                                    sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700 }}
                                  />
                                )}
                                
                                {tag && (
                                  <Chip
                                    label={tag}
                                    size="small"
                                    variant="outlined"
                                    sx={{ height: 18, fontSize: '0.6rem', opacity: 0.8 }}
                                  />
                                )}

                                <Chip
                                  label={article.mediaType || 'Sem tipo de mídia'}
                                  size="small"
                                  color={sortField === 'mediaType' ? 'primary' : 'default'}
                                  variant="outlined"
                                  sx={{ height: 18, fontSize: '0.6rem', fontWeight: 600 }}
                                />

                                <Chip
                                  label={new Date(article.date || article.createdAt).toLocaleDateString('pt-BR')}
                                  size="small"
                                  color={sortField === 'date' ? 'primary' : 'default'}
                                  variant="outlined"
                                  sx={{ height: 18, fontSize: '0.6rem', fontWeight: 600 }}
                                />

                                {article.valuation && (
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: 'success.main',
                                      fontWeight: 700,
                                      bgcolor: isDarkMode ? 'rgba(129, 201, 149, 0.1)' : 'rgba(56, 142, 60, 0.05)',
                                      px: 0.8,
                                      py: 0.2,
                                      borderRadius: 1,
                                    }}
                                  >
                                    {typeof article.valuation === 'number'
                                      ? article.valuation.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 0 })
                                      : article.valuation}
                                  </Typography>
                                )}
                              </Box>
                            </Box>

                            {/* Botão remover */}
                            <Tooltip title="Remover da newsletter">
                              <IconButton
                                size="small"
                                onClick={() => handleRemove(article._id)}
                                sx={{
                                  color: 'text.disabled',
                                  '&:hover': { color: 'error.main', bgcolor: 'error.lighter' },
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>

                            {/* Indicador visual de arraste lateral */}
                            {snapshot.isDragging && (
                              <Box 
                                sx={{ 
                                  position: 'absolute', 
                                  left: 0, 
                                  top: 0, 
                                  bottom: 0, 
                                  width: 4, 
                                  background: getPrimaryBackground() 
                                }} 
                              />
                            )}
                          </Paper>
                        )}
                      </Draggable>
                    );
                  })}
                  {provided.placeholder}
                </Box>
              )}
            </Droppable>
          </DragDropContext>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 3, gap: 2, bgcolor: isDarkMode ? 'rgba(0,0,0,0.1)' : 'rgba(0,0,0,0.02)' }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" color="text.secondary" fontWeight={500}>
            A ordem será salva ao confirmar.
          </Typography>
        </Box>
        <Button 
          onClick={onClose} 
          variant="text" 
          sx={{ color: 'text.secondary', fontWeight: 600 }}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={orderedArticles.length === 0}
          startIcon={<CheckCircleIcon />}
          sx={{
            background: getPrimaryBackground(),
            color: 'primary.contrastText',
            fontWeight: 800,
            px: 4,
            py: 1,
            borderRadius: 2,
            boxShadow: isPrimaryGradient ? '0 4px 14px rgba(0,0,0,0.25)' : undefined,
            '&:hover': {
              opacity: 0.9,
              transform: 'translateY(-1px)',
              boxShadow: isPrimaryGradient ? '0 6px 20px rgba(0,0,0,0.3)' : undefined,
            },
            transition: 'all 0.2s ease',
          }}
        >
          Confirmar Ordem
        </Button>
      </DialogActions>
    </PlatformDialog>
  );
};

export default NewsletterFinalOrderDialog;
